import { Box, Collapse, HStack, Icon, useDisclosure } from "@chakra-ui/react";
import { useEffect } from "react";
import { FiChevronRight } from "react-icons/fi";
import CDS from "design-system";
import { PrefixId } from "features/Resources/Shared/models";
import { MeetingDayBucket } from "features/Resources/Meeting/helpers";
import DaySection from "./DaySection";

interface PreviousSectionProps {
  days: MeetingDayBucket[];
  onExpand: (prefixId: PrefixId) => void;
  startDate?: Date | string;
  showSkippedExpanded?: boolean;
  /** Open by default (e.g. when there are no current/upcoming meetings to anchor on). */
  defaultOpen?: boolean;
}

/** Collapses past days into one expandable "Previous" row summarizing the meeting count. */
export default function PreviousSection({
  days,
  onExpand,
  startDate,
  showSkippedExpanded,
  defaultOpen,
}: PreviousSectionProps) {
  const { isOpen, onToggle, onOpen } = useDisclosure({ defaultIsOpen: defaultOpen });

  // Force open when the parent asks (Skipped filter on, or no upcoming days) without
  // clobbering a manual collapse the rest of the time.
  useEffect(() => {
    if (defaultOpen) onOpen();
  }, [defaultOpen, onOpen]);

  if (days.length === 0) return null;

  const meetings = days.reduce((n, d) => n + d.scheduled.length, 0);
  const missed = days.reduce((n, d) => n + d.skipped.length, 0);

  return (
    <Box w="100%">
      <HStack justify="space-between" align="center" px={4} py={2.5} as="button" type="button" onClick={onToggle} w="100%">
        <HStack spacing={2} align="center" minW={0}>
          <Icon
            as={FiChevronRight}
            boxSize={3.5}
            color="fg.subtle"
            transform={isOpen ? "rotate(90deg)" : undefined}
            transition="transform 0.15s"
          />
          <CDS.Text16 fontWeight="semibold">Previous</CDS.Text16>
          <CDS.Text12 color="fg.muted" noOfLines={1}>
            · {meetings} {meetings === 1 ? "meeting" : "meetings"}
          </CDS.Text12>
        </HStack>
        {missed > 0 && (
          <Box px={2} py={0.5} borderRadius="md" bg="red.50" flexShrink={0}>
            <CDS.Text12 fontWeight="semibold" color="red.600">
              {missed} missed
            </CDS.Text12>
          </Box>
        )}
      </HStack>
      <Collapse in={isOpen} animateOpacity unmountOnExit>
        {days.map((day) => (
          <DaySection
            key={day.key}
            day={day}
            onExpand={onExpand}
            startDate={startDate}
            showSkippedExpanded={showSkippedExpanded}
          />
        ))}
      </Collapse>
    </Box>
  );
}
